import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import HireTalentLayout from "../../layouts/HireTalentLayout";
import Loader from "../../components/loader";
import ErrorMessage from "../../components/form/ErrorMessage";
import TableHeader from "../../components/table/tableHeader";
import TicketDetailsModal from "../../components/Modals/common/TicketDetailsModal";
import PaginationDropdown from "../../components/table/PaginationDropdown";
import ReactPagination from "../../components/table/ReactPagination";
import toast from "react-hot-toast";
import {
  fetchHireTalentTickets,
  raiseHireTalentTicket,
} from "../../redux/features/hire-talent/actions/authAction";

const Settings = () => {
  const dispatch = useDispatch();
  const { isLoading, subLoading, ticketList } = useSelector(
    (state) => state.hireTalent.auth
  );

  const [accountInformation, setAccountInformation] = useState("Ticket");
  const [isRaiseForm, setIsRaiseForm] = useState(false);
  const [ticketImages, setTicketImages] = useState([]);
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketListLoading, setTicketListLoading] = useState(false);
  const hasFetchedTickets = useRef(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [listPerPages, setListPerPages] = useState(5);

  const ticketInitialValues = {
    title: "",
    description: "",
    category: "",
  };

  const paginatedTicketList =
    ticketList?.slice(
      currentPage * listPerPages,
      (currentPage + 1) * listPerPages
    ) || [];

  const handlePageClick = (data) => {
    setCurrentPage(data?.selected);
  };

  useEffect(() => {
    if (accountInformation !== "Ticket" || hasFetchedTickets.current) return;
    setTicketListLoading(true);
    dispatch(fetchHireTalentTickets())
      .finally(() => setTicketListLoading(false));
    hasFetchedTickets.current = true;
  }, [accountInformation, dispatch]);

  useEffect(() => {
    return () => {
      ticketImages.forEach((item) => {
        if (item?.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
  }, [ticketImages]);

  useEffect(() => {
    setCurrentPage(0);
  }, [ticketList, listPerPages]);

  const handleTicketImagesSelect = (event) => {
    const files = Array.from(event.currentTarget.files || []);
    const imageFiles = files.filter((file) => file.type?.startsWith("image/"));
    if (files.length && imageFiles.length !== files.length) {
      toast.error("Only image files are allowed.");
    }
    if (imageFiles.length) {
      const nextItems = imageFiles.map((file) => ({
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
      }));
      setTicketImages((prev) => [...prev, ...nextItems]);
    }
    event.currentTarget.value = "";
  };

  const handleTicketImageRemove = (index) => {
    setTicketImages((prev) => {
      const next = [...prev];
      const removed = next.splice(index, 1);
      if (removed[0]?.preview) {
        URL.revokeObjectURL(removed[0].preview);
      }
      return next;
    });
  };

  const handleTicketSubmit = (values, actions) => {
    if (ticketSubmitting) return;
    setTicketSubmitting(true);
    const formData = new FormData();
    formData.append("title", values.title || "");
    formData.append("description", values.description || "");
    formData.append("category", values.category || "");
    ticketImages.forEach((item) => {
      if (item?.file instanceof File) {
        formData.append("images", item.file);
      }
    });

    const callback = (response) => {
      if (response?.success) {
        actions.resetForm();
        setTicketImages([]);
        setIsRaiseForm(false);
        setTicketListLoading(true);
        dispatch(fetchHireTalentTickets())
          .finally(() => setTicketListLoading(false));
      }
      setTicketSubmitting(false);
    };

    dispatch(raiseHireTalentTicket({ payload: formData, callback }));
  };

  if (ticketListLoading || (isLoading && isRaiseForm)) {
    return <Loader />;
  }

  return (
    <HireTalentLayout>
      <div className="mt-4 ct_px_40 pb-5">
        <div className="ct_mb_20">
          <h4 className="ct_fs_20_new ct_fw_600 ct_darkblue_text">Settings</h4>
          <p className="mb-0 ct_grey_text">
            Manage your account settings and support tickets
          </p>
        </div>

        <div className="row ct_pt_20 ct_border_top_1_op_10 ct_mt_20">
          <div className="col-lg-3 mb-4 ">
            <div className="ct_setting_tabs">
              <div
                className="nav flex-column nav-pills me-3"
                id="v-pills-tab"
                role="tablist"
                aria-orientation="vertical"
              >
                <button
                  className={`nav-link ${accountInformation == "Ticket" && "active"
                    }`}
                  id="v-pills-Ticket-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#v-pills-Ticket"
                  type="button"
                  role="tab"
                  aria-controls="v-pills-Ticket"
                  aria-selected="false"
                  onClick={() => setAccountInformation("Ticket")}
                >
                  Report an issue
                </button>
              </div>
            </div>
          </div>
          <div className="col-lg-9  mb-4 ">
            <div className="tab-content" id="v-pills-tabContent">
              <div
                className={`tab-pane fade ${accountInformation == "Ticket" && "show active"
                  }`}
                id="v-pills-Ticket"
                role="tabpanel"
                aria-labelledby="v-pills-Ticket-tab"
              >
                <div className="">
                  <div className="mb-4 d-flex align-items-center justify-content-between">
                    <div className="">
                      <h6 className="ct_fs_18 mb-0">
                        Raise Your NUVIO Support Ticket
                      </h6>
                      <p className="ct_fs_14 ct_grey_text">
                        We've got you back! Fill this in and our team will jump
                        on it fast.
                      </p>
                    </div>
                    {!isRaiseForm && (
                      <button
                        className="ct_yellow_btn ct_sky_blue_btn ct_h_44"
                        onClick={() => setIsRaiseForm(true)}
                      >
                        Submit & Let's Fix It!
                      </button>
                    )}
                  </div>

                  {!isRaiseForm ? (
                    <div className="table-responsive ct_custom_table ct_custom_table2 pt-2">
                      <table className="table">
                        <TableHeader
                          data={[
                            "#",
                            "Title",
                            "Description",
                            "Category",
                            "Action",
                          ]}
                        />
                        {paginatedTicketList?.length > 0 && (
                          <tbody>
                            {paginatedTicketList?.map((item, index) => (
                              <tr key={item?.ticket_id || index}>
                                <td>{currentPage * listPerPages + index + 1}</td>
                                <td className="text-capitalize">
                                  {item?.title || "#N/A"}{" "}
                                </td>
                                <td>
                                  <span className="ct_pverlay_text d-inline-block">
                                    {item?.description || "#N/A"}
                                  </span>
                                </td>
                                <td>{item?.category || "#N/A"}</td>
                                <td>
                                  <div className="ct_right_dropdown dropdown">
                                    <i
                                      style={{
                                        cursor: "pointer",
                                      }}
                                      onClick={() => setActiveTicket(item)}
                                      className="fa-solid fa-eye"
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        )}
                      </table>
                      {ticketList?.length <= 0 && (
                        <div className="text-center ct_grey_text py-4">
                          No tickets found.
                        </div>
                      )}
                      {ticketList?.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-4 ">
                          <div>
                            <PaginationDropdown
                              onChange={(val) => {
                                setListPerPages(val);
                                setCurrentPage(0);
                              }}
                            />
                          </div>
                          <div>
                            <ReactPagination
                              pageCount={Math.ceil(
                                (ticketList?.length || 0) / listPerPages
                              )}
                              onPageChange={handlePageClick}
                              currentPage={currentPage}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Formik
                      initialValues={ticketInitialValues}
                      validate={(values) => {
                        const errors = {};
                        if (!values.title?.trim()) {
                          errors.title = "Title is required";
                        }
                        if (!values.description?.trim()) {
                          errors.description = "Description is required";
                        }
                        if (!values.category?.trim()) {
                          errors.category = "Category is required";
                        }
                        return errors;
                      }}
                      onSubmit={handleTicketSubmit}
                    >
                      {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        resetForm,
                      }) => (
                        <form className="mt-4" onSubmit={handleSubmit}>
                          <div className="row">
                            <div className="col-md-12">
                              <div className="form-group mb-4">
                                <label className="mb-2 ct_fs_14 ct_grey_text">
                                  What's Happening? (Title)
                                </label>
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className="form-control ct_input  pe-5"
                                    placeholder="What's Happening?"
                                    id="title"
                                    value={values.title}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                  />
                                </div>
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName="title"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-12">
                              <div className="form-group mb-4">
                                <label className="mb-2 ct_fs_14 ct_grey_text">
                                  Tell us more, share the full story—steps, errors, screenshots—everything that helps us fix it faster.
                                </label>
                                <div className="position-relative">
                                  <textarea
                                    rows="4"
                                    className="form-control ct_input h-auto"
                                    placeholder="Tell us more, share the full story—steps, errors, screenshots—everything that helps us fix it faster."
                                    id="description"
                                    value={values.description}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                  />
                                </div>
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName="description"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group mb-4">
                                <label className="ct_fs_14 ct_darkblue_text mb-2">
                                  Category
                                </label>
                                <div className="d-flex flex-column gap-2 position-relative">
                                  <select
                                    className="form-control ct_fs_12 ct_input"
                                    id="category"
                                    value={values.category}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                  >
                                    <option value="">--- Select category ---</option>
                                    <option value="Tech Trouble">
                                      Tech Trouble
                                    </option>
                                    <option value="Billing/Payment">
                                      Billing/Payment
                                    </option>
                                    <option value="Account/Login">
                                      Account/Login
                                    </option>
                                    <option value="Feature Request">
                                      Feature Request
                                    </option>
                                    <option value="Something Else">
                                      Something Else
                                    </option>
                                  </select>
                                  <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                                </div>
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName={"category"}
                                />
                              </div>
                            </div>
                            <div className="col-md-12">
                              <div className="form-group mb-4">
                                <label className="ct_fs_14 ct_darkblue_text mb-2">
                                  Attachments (Images)
                                </label>
                                <div className="ct_ticket_upload_area">
                                  <label className="ct_upload_btn ct_ticket_upload_btn">
                                    <i className="fa-solid fa-cloud-arrow-up"></i>
                                    Upload Images
                                    <input
                                      className="ct_file_input"
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={handleTicketImagesSelect}
                                    />
                                  </label>
                                  <p className="ct_fs_12 ct_grey_text mb-0">
                                    You can upload multiple screenshots or
                                    images.
                                  </p>
                                </div>
                                {ticketImages.length > 0 && (
                                  <div className="ct_ticket_upload_grid mt-3">
                                    {ticketImages.map((item, index) => (
                                      <div
                                        className="ct_ticket_thumb"
                                        key={`${item.name}-${index}`}
                                      >
                                        <img
                                          src={item.preview}
                                          alt={item.name}
                                        />
                                        <button
                                          type="button"
                                          className="ct_ticket_thumb_remove"
                                          onClick={() =>
                                            handleTicketImageRemove(index)
                                          }
                                        >
                                          <i className="fa-solid fa-xmark"></i>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="d-flex align-items-center  gap-3 mt-4">
                            <button
                              className="ct_yellow_btn ct_outline_light_grey px-5"
                              type="button"
                              onClick={() => {
                                resetForm();
                                setTicketImages([]);
                                setIsRaiseForm(false);
                              }}
                            >
                              Discard
                            </button>
                            <button
                              className="ct_yellow_btn px-5"
                              type="submit"
                              disabled={ticketSubmitting || isLoading}
                            >
                              {isLoading ? "Submitting..." : "Submit Ticket"}
                            </button>
                          </div>
                        </form>
                      )}
                    </Formik>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {activeTicket && (
        <TicketDetailsModal
          isOpen={!!activeTicket}
          onClose={() => setActiveTicket(null)}
          ticket={activeTicket}
        />
      )}
    </HireTalentLayout>
  );
};

export default Settings;
